import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { GearSix, List, X, Plus, Files, SignOut, Lock, Gavel, ChartBar, Bag, ClipboardText } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';

export default function Layout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const { user, logout } = useAuth();

  const navItems = [
    { path: '/', label: 'Quotations', icon: Files },
    { path: '/purchase-orders', label: 'Purchase Orders', icon: Bag },
    { path: '/proforma-invoices', label: 'Proforma Invoices', icon: ClipboardText },
    { path: '/create-quotation', label: 'New Quotation', icon: Plus },
    { path: '/tenders', label: 'Tender Monitoring', icon: Gavel },
    { path: '/tender-analytics', label: 'Tender Analytics', icon: ChartBar },
    { path: '/admin', label: 'Administration', icon: GearSix },
  ];

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Sidebar */}
      <aside className={`no-print fixed lg:static inset-y-0 left-0 z-50 w-60 bg-[#1c1917] text-stone-300 transform transition-transform duration-200 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
      }`}>
        <div className="flex flex-col h-full">
          <div className="px-6 py-8">
            <h1 className="text-xl font-bold tracking-tight text-white" data-testid="app-logo" style={{ fontFamily: "'DM Serif Display', serif" }}>
              Quotent
            </h1>
            <p className="text-[11px] text-stone-500 mt-1 uppercase tracking-[0.15em] font-medium">Quotation System</p>
          </div>

          <nav className="flex-1 px-3 space-y-0.5">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`flex items-center gap-3 px-4 py-2.5 text-[13px] font-medium transition-all duration-150 ${
                    isActive
                      ? 'bg-white/10 text-white'
                      : 'text-stone-400 hover:bg-white/5 hover:text-stone-200'
                  }`}
                >
                  <Icon size={18} weight={isActive ? 'fill' : 'regular'} />
                  {item.label}
                </Link>
              );
            })}

            <div className="pt-4 mt-4 border-t border-white/5">
              <Link to="/control-panel" onClick={() => setSidebarOpen(false)} data-testid="nav-control-panel"
                className="flex items-center gap-3 px-4 py-2.5 text-[13px] font-medium text-stone-500 hover:bg-white/5 hover:text-stone-300 transition-all duration-150">
                <Lock size={18} /> Control Panel
              </Link>
            </div>
          </nav>

          <div className="px-3 pb-2">
            {user && (
              <div className="flex items-center justify-between px-4 py-2.5">
                <span className="text-xs text-stone-400 truncate">{user.username}</span>
                <button onClick={logout} data-testid="logout-button" title="Sign out"
                  className="p-1 text-stone-500 hover:text-red-400 transition-colors">
                  <SignOut size={16} />
                </button>
              </div>
            )}
          </div>

          <div className="px-6 py-4 border-t border-white/5">
            <p className="text-[10px] text-stone-500 mb-1">Developed by:- Abhinav Mangla</p>
            <p className="text-[10px] text-stone-600 uppercase tracking-wider">v2.0</p>
          </div>
        </div>
      </aside>

      {/* Mobile toggle */}
      <button
        data-testid="mobile-menu-toggle"
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="no-print lg:hidden fixed top-4 left-4 z-50 p-2 bg-[#1c1917] text-white rounded"
      >
        {sidebarOpen ? <X size={20} /> : <List size={20} />}
      </button>

      {sidebarOpen && (
        <div className="no-print lg:hidden fixed inset-0 bg-black/30 z-40" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Main content */}
      <main className="flex-1 overflow-auto bg-[#fafaf9]">
        <div className="p-6 sm:p-8 lg:p-10 max-w-[1200px]">
          {children}
        </div>
      </main>
    </div>
  );
}
